import { z } from 'zod';

import { EntityBaseSchema, IdSchema } from '../../base/primitives';
import { functionsReferencesAdapter } from './function.adapters';
import {
  DelayResponseDurationSchema,
  DelayResponseMessageSchema,
  FunctionArchivedSchema,
  FunctionCodeSchema,
  FunctionDescriptionSchema,
  FunctionErrorSchema,
  FunctionNameSchema,
  FunctionParameterDescriptionSchema,
  FunctionParameterNameSchema,
  FunctionParameterTypeSchema,
  FunctionReferencesSchema,
  LatencyControlEnabledSchema,
  LatencyControlInitialDelaySchema,
  LatencyControlIntervalSchema,
} from './function.schema';

/**
 * Command ID schemas - used for identifying specific functions in commands
 */
export const FunctionIdSchema = z.object({
  functionId: IdSchema.describe('ID of the function this command targets'),
});

/**
 * Command parameter schemas - without metadata fields that are added during processing
 */
const BaseFunctionParameterCommandSchema = z.object({
  name: FunctionParameterNameSchema.describe('Name of the parameter'),
  description: FunctionParameterDescriptionSchema.describe(
    'Description of what this parameter does',
  ),
  type: FunctionParameterTypeSchema.describe('Type of the parameter'),
});

export const FunctionCreateParameterSchema = FunctionIdSchema.extend({
  parameter: z
    .object({
      id: IdSchema.describe('Unique identifier for the parameter'),
    })
    .merge(BaseFunctionParameterCommandSchema)
    .describe('Parameter data to create'),
}).describe('Command to create a new function parameter');

export const FunctionUpdateParameterSchema = FunctionIdSchema.extend({
  parameter: z
    .object({
      id: IdSchema.describe('ID of the parameter to update'),
    })
    .merge(BaseFunctionParameterCommandSchema.partial())
    .describe('Parameter data to update'),
}).describe('Command to update an existing function parameter');

export const FunctionParameterCommandSchema = z
  .object({
    id: IdSchema.describe('Unique identifier for the parameter'),
  })
  .merge(BaseFunctionParameterCommandSchema);

/**
 * Command delay response schemas - without metadata fields
 */
const BaseDelayResponseCommandSchema = z.object({
  message: DelayResponseMessageSchema,
  duration: DelayResponseDurationSchema,
  references: z
    .object({
      variables: z
        .record(z.boolean())
        .describe('Map of variable IDs referenced in this message'),
    })
    .optional()
    .describe('Optional references to variables used in the message'),
});

export const CreateDelayResponseSchema = FunctionIdSchema.extend({
  delayResponse: z
    .object({
      id: IdSchema.describe('Unique identifier for the delay response'),
    })
    .merge(BaseDelayResponseCommandSchema)
    .describe('Delay response data to create'),
}).describe('Command to create a new delay response');

export const UpdateDelayResponseSchema = FunctionIdSchema.extend({
  delayResponse: z
    .object({
      id: IdSchema.describe('ID of the delay response to update'),
    })
    .merge(BaseDelayResponseCommandSchema.partial())
    .describe('Delay response data to update'),
}).describe('Command to update an existing delay response');

export const DelayResponseCommandSchema = z
  .object({
    id: IdSchema.describe('Unique identifier for the delay response'),
  })
  .merge(BaseDelayResponseCommandSchema);

/**
 * Command latency control schemas
 */
const BaseLatencyControlCommandSchema = z.object({
  enabled: LatencyControlEnabledSchema,
  delayResponses: z
    .array(DelayResponseCommandSchema)
    .describe('List of delay response messages'),
  initialDelay: LatencyControlInitialDelaySchema,
  interval: LatencyControlIntervalSchema,
});

const UpdateDelayResponsesSchema = z.object({
  delayResponses: z
    .array(DelayResponseCommandSchema)
    .describe('Updated list of delay responses'),
});

export const UpdateLatencyControlSchema = FunctionIdSchema.merge(
  BaseLatencyControlCommandSchema.partial(),
)
  .extend({
    delayResponses: UpdateDelayResponsesSchema,
  })
  .describe('Command to update function latency control configuration');

export const LatencyControlCommandSchema =
  BaseLatencyControlCommandSchema.describe(
    'Latency control configuration for commands',
  );
/**
 * Base function schema for commands - without metadata
 */
export const BaseFunctionSchema = z
  .object({
    name: FunctionNameSchema,
    description: FunctionDescriptionSchema,
    parameters: z
      .array(FunctionParameterCommandSchema)
      .describe('Parameters this function accepts'),
    code: FunctionCodeSchema,
    archived: FunctionArchivedSchema,
    errors: z
      .array(FunctionErrorSchema)
      .describe('Validation errors for this function'),
  })
  .describe('Base schema for function commands without metadata');

/**
 * CRUD Command Schemas
 */
export const CreateFunctionCommandSchema = z
  .object({
    id: IdSchema.describe('Unique identifier for the new function'),
  })
  .merge(BaseFunctionSchema)
  .extend({
    latencyControl: LatencyControlCommandSchema.optional().describe(
      'Optional latency control configuration',
    ),
    references: FunctionReferencesSchema.describe(
      'References to other entities',
    ),
    archived: z.boolean().default(false),
  })
  .describe('Command to create a new global function');

export const FunctionCreateFunctionSchema = CreateFunctionCommandSchema.omit({
  references: true,
}).extend({
  references: FunctionReferencesSchema.default(
    functionsReferencesAdapter.createInitialState(),
  ),
});

export type CreateFunctionPayload = z.input<
  typeof FunctionCreateFunctionSchema
>;

const ParametersUpdateSchema = z
  .object({
    parameters: z.array(FunctionParameterCommandSchema),
  })
  .describe('Parameters update wrapper');

export const ErrorsUpdateSchema = z
  .object({
    errors: z.array(FunctionErrorSchema),
  })
  .describe('Errors update wrapper');

export const UpdateFunctionCommandSchema = z
  .object({
    id: IdSchema.describe('ID of the function to update'),
  })
  .merge(
    BaseFunctionSchema.partial().extend({
      description: FunctionDescriptionSchema.optional(),
      parameters: ParametersUpdateSchema.optional(),
      errors: ErrorsUpdateSchema.optional(),
      references: FunctionReferencesSchema.optional(),
    }),
  )
  .describe(
    'Command to update an existing function. Only include fields to change.',
  );

export const DeleteFunctionCommandSchema = z
  .object({
    id: IdSchema.describe('ID of the function to delete'),
  })
  .describe('Command to delete a function');

export const FunctionUpdateFunctionSchema = EntityBaseSchema.merge(
  BaseFunctionSchema.partial().extend({
    description: z.string().optional(),
    parameters: ParametersUpdateSchema.optional(),
    errors: ErrorsUpdateSchema.optional(),
    references: FunctionReferencesSchema.optional(),
  }),
);

/**
 * Type exports
 */
export type FunctionId = z.infer<typeof FunctionIdSchema>;
export type FunctionCreateParameter = z.infer<
  typeof FunctionCreateParameterSchema
>;
export type FunctionUpdateParameter = z.infer<
  typeof FunctionUpdateParameterSchema
>;
export type FunctionParameterCommand = z.infer<
  typeof FunctionParameterCommandSchema
>;
export type CreateDelayResponse = z.infer<typeof CreateDelayResponseSchema>;
export type UpdateDelayResponse = z.infer<typeof UpdateDelayResponseSchema>;
export type DelayResponseCommand = z.infer<typeof DelayResponseCommandSchema>;
export type UpdateLatencyControl = z.infer<typeof UpdateLatencyControlSchema>;
export type LatencyControlCommand = z.infer<typeof LatencyControlCommandSchema>;
export type BaseFunction = z.infer<typeof BaseFunctionSchema>;
export type CreateFunctionCommand = z.infer<typeof CreateFunctionCommandSchema>;
export type UpdateFunctionCommand = z.infer<typeof UpdateFunctionCommandSchema>;
export type DeleteFunctionCommand = z.infer<typeof DeleteFunctionCommandSchema>;
export type UpdateFunctionPayload = z.infer<
  typeof FunctionUpdateFunctionSchema
>;
